import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { authRepository } from '../api/auth-repository'
import { ChangePasswordModal } from './change-password-modal'
import { logger } from '@/core/logger'

/**
 * Bundles the user-menu account affordances: the Change-password modal
 * plus the auth-repository lookups the menu shares (password provider,
 * current email, verification state).
 *
 * Two capabilities have left this menu by product decision:
 *  - Display name: owner-managed from the Personal (`/staff`) page.
 *  - Account deletion: relocated to the Personal page, gated to users
 *    with the right permission (see `delete-account-modal.tsx`).
 *
 * Lives in `features/auth/` so `shared/components/app-shell.tsx` imports
 * one `useAccountModals()` hook instead of wiring modals individually.
 * The hook returns openers for the dropdown plus a `render` element to
 * mount once.
 */
export interface UseAccountModalsResult {
  /** True when at least one modal is mounted — useful if the caller
   *  needs to suppress backdrop dismissals on the surrounding sheet. */
  anyOpen: boolean
  openPassword: () => void
  /** Whether the signed-in user has email/password provider (vs Google).
   *  The dropdown surfaces "Cambiar contraseña" only when true. */
  hasPasswordProvider: boolean
  /** From `firebase.User.emailVerified`. False ⇒ the menu can show a
   *  "Reenviar verificación" item driven by `resendVerification()`. */
  emailVerified: boolean
  accountEmail: string
  resending: boolean
  resendVerification: () => Promise<void>
  /** The mounted modals — render once near the menu. */
  render: ReactNode
}

export function useAccountModals(): UseAccountModalsResult {
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [resending, setResending] = useState(false)

  const hasPasswordProvider = authRepository.hasPasswordProvider()
  const accountEmail = authRepository.currentUser?.email ?? ''
  // `User.emailVerified` is a snapshot — Firebase Auth doesn't refresh it
  // when the user clicks the verification link in another tab. Without an
  // explicit reload, the menu keeps showing "Reenviar email de verificación"
  // forever for users who verified after first sign-in. Reload once on
  // mount; the menu re-renders when state lands.
  const [emailVerified, setEmailVerified] = useState<boolean>(
    () => authRepository.currentUser?.emailVerified ?? true,
  )
  useEffect(() => {
    let cancelled = false
    void authRepository
      .reloadCurrentUser()
      .then(() => {
        if (cancelled) return
        setEmailVerified(authRepository.currentUser?.emailVerified ?? true)
      })
      .catch((e: unknown) => {
        // A stale session (e.g. auth/user-token-expired) throws here; the
        // app-level session listener already handles the sign-out redirect,
        // so this reload is just a best-effort refresh — swallow, don't crash.
        logger.d('account-modals: reloadCurrentUser failed', e)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function resendVerification() {
    if (resending) return
    setResending(true)
    try {
      await authRepository.sendVerificationEmail()
      toast.success('Email de verificación enviado')
    } catch (e) {
      // This menu has no cooldown UI (unlike email-verification-page.tsx),
      // so a rapid double-click here is the one path that actually hits the
      // server-side throttle in send_custom_verification_email — worth its
      // own message rather than the generic fallback.
      const code = (e as { code?: string })?.code
      toast.error(
        code === 'functions/resource-exhausted'
          ? 'Espera unos segundos antes de reenviarlo.'
          : 'No pudimos enviar el email. Inténtalo en unos minutos.',
      )
    } finally {
      setResending(false)
    }
  }

  const render = passwordOpen ? (
    <ChangePasswordModal
      hasPasswordProvider={hasPasswordProvider}
      onClose={() => setPasswordOpen(false)}
    />
  ) : null

  return {
    anyOpen: passwordOpen,
    openPassword: () => setPasswordOpen(true),
    hasPasswordProvider,
    emailVerified,
    accountEmail,
    resending,
    resendVerification,
    render,
  }
}
